import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { FormControl, Checkbox, ToggleButton, InputLabel, MenuItem, Select, Chip, Tooltip, Paper, Grid } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { List, Map } from 'immutable';

import Button from 'src/component/UI/Button';
import FloatingActionBar from 'src/component/UI/FloatingActionBar/FloatingActionBar';
import ExportMenu from 'src/component/UI/ExportMenu';
import CsvIcon from 'src/assets/icons/csv.svg';
import PdfIcon from 'src/assets/icons/pdf.svg';
import FilterBar from 'src/component/UI/FilterBar';
import DataTable from 'src/component/UI/DataTable';
import PermissionFence from 'src/component/PermissionFence';
import CommissioningStatusIcon from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningStatusIcon';
import CommissioningCommLoopReport from 'src/screen/Protected/DeviceScreen/CommissioningCommLoop/CommissioningCommLoopReport';
import CommissioningCircuitReport from 'src/screen/Protected/DeviceScreen/CommissioningCircuit/CommissioningCircuitReport';
import CommissioningControllerReport from 'src/screen/Protected/DeviceScreen/CommissioningController/CommissioningControllerReport';
import CommissioningTypeSelectDialog from './CommissioningTypeSelectDialog';
import LinkDeviceDialog from './LinkDeviceDialog';

import {
  fetchCommissioningReports,
  openCheckSheet,
  closeCheckSheet,
  linkDeviceToCheckSheet,
  downloadCommissioningPdf,
  downloadBulkCommissioningPdf,
  downloadCommissioningSummaryPdf,
  fetchReportsCsvExport,
} from 'src/module/checkSheet/action';
import {
  getCommissioningReports,
  getCommissioningReportsLoading,
  getCommissioningReportsTotal,
  getCommissioningReportsTotalPages,
  getCheckSheetReportIsOpen,
  getCommissioningLastLinkSuccess,
  getBulkCommissioningPdfLoading,
  getSummaryCommissioningPdfLoading,
  getCommissioningPdfLoadingByReportId,
  getExportReportsLoading,
} from 'src/module/checkSheet/selector';
import { usePDFServices } from 'src/hooks/useReportData';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/VisibilitySharp';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import { formatDate } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';
import toast from 'src/utils/toast';
import variables from 'src/style/variable/variables.module.scss';
import './CommissioningScreen.scss';
import { COMMISSIONING_DEVICE_TYPE_MAP, COMMISSIONING_TYPE_LABEL_MAP, COMMISSIONING_STATUS_OPTIONS } from './commissioningTypes';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';


const pdfIconStyles = { color: variables.primarySolid };


function PdfDownloadButton ({ reportId, checkSheetType, deviceTag }: { reportId: string; checkSheetType: string; deviceTag: string }) {
  const dispatch = useDispatch();
  const loading = useSelector(getCommissioningPdfLoadingByReportId(reportId));
  return (
    <Button
      compact
      icon={<FileDownloadIcon style={pdfIconStyles} />}
      spinner={<LoadingSpinner color={variables.primarySolid} size={16} />}
      loading={loading}
      disabled={loading}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(downloadCommissioningPdf(reportId, checkSheetType, deviceTag));
      }}
    />
  );
}

export default function CommissioningScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const userPagePreference = useSelector(getTablePagePreferences('commissioning-table'));
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [sort, setSort] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const selectedFilters: any = useSelector(getDevicesSelectedFilters);

  const getFilterParams = () => ({
    unit: selectedFilters.get('unit') || undefined,
    system: selectedFilters.get('system') || undefined,
    commodity: selectedFilters.get('commodity') || undefined,
    location: selectedFilters.get('location') || undefined,
    priority: selectedFilters.get('priority') || undefined,
    controllerId: selectedFilters.get('controller') || undefined,
    deadleg: selectedFilters.get('deadleg') === 'Yes' ? 'true' : selectedFilters.get('deadleg') === 'No' ? 'false' : undefined,
    deviceGroup: selectedFilters.get('deviceGroup') || undefined,
  });

  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());

  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkReportId, setLinkReportId] = useState(null);
  const [linkReportType, setLinkReportType] = useState(null);


  const lastLinkSuccess = useSelector(getCommissioningLastLinkSuccess);

  const commLoopV2Open = useSelector(getCheckSheetReportIsOpen('commissioning-comm-loop-v2'));
  const circuitOpen = useSelector(getCheckSheetReportIsOpen('commissioning-circuit'));
  const controllerOpen = useSelector(getCheckSheetReportIsOpen('commissioning-controller'));

  const anyOpen = commLoopV2Open || circuitOpen || controllerOpen;
  const wasOpen = useRef(false);

  useEffect(() => {
    if (anyOpen) {
      wasOpen.current = true;
    } else if (wasOpen.current) {
      wasOpen.current = false;
      dispatch(fetchCommissioningReports({
        page,
        pageSize,
        sort,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        unlinkedOnly: unlinkedOnly || undefined,
        ...getFilterParams(),
      }));
    }
  }, [anyOpen]);

  useEffect(() => {
    if (lastLinkSuccess > 0) {
      dispatch(fetchCommissioningReports({
        page,
        pageSize,
        sort,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        unlinkedOnly: unlinkedOnly || undefined,
        ...getFilterParams(),
      }));
    }
  }, [lastLinkSuccess]);

  const reports = useSelector(getCommissioningReports);
  const loading = useSelector(getCommissioningReportsLoading);
  const total = useSelector(getCommissioningReportsTotal);
  const totalPages = useSelector(getCommissioningReportsTotalPages);

  const { usePDFServicesValue: pdfServicesEnabled } = usePDFServices();
  const bulkDownloading = useSelector(getBulkCommissioningPdfLoading);
  const summaryDownloading = useSelector(getSummaryCommissioningPdfLoading);

  const BULK_DOWNLOAD_WARN_LIMIT = 10;
  const BULK_DOWNLOAD_HARD_LIMIT = 20;

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
  };

  useEffect(() => {
    dispatch(fetchCommissioningReports({
      page,
      pageSize,
      sort,
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      unlinkedOnly: unlinkedOnly || undefined,
      ...getFilterParams(),
    }));
  }, [page, pageSize, sort, typeFilter, statusFilter, unlinkedOnly, selectedFilters, dispatch]);

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };



  const onClickCreate = () => {
    setShowTypeDialog(true);
  };

  const handleTypeSelected = (type, deviceId) => {
    setSelectedType(type);
    setSelectedDeviceId(deviceId);
    setSelectedReportId(null);
    setShowTypeDialog(false);
    dispatch(openCheckSheet(type));
  };

  const handleClose = (type) => {
    dispatch(closeCheckSheet(type));
    setSelectedReportId(null);
    setSelectedDeviceId(null);
    setSelectedType(null);
    dispatch(fetchCommissioningReports({
      page,
      pageSize,
      sort,
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      ...getFilterParams(),
    }));
  };

  const handleToggleSelect = useCallback((reportId: string) => {
    setSelectedReportIds(prev => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedReportIds(new Set(reports.map((r: any) => r.get('id')).toArray()));
    } else {
      setSelectedReportIds(new Set());
    }
  }, [reports]);

  const prevBulkDownloading = useRef(false);
  useEffect(() => {
    if (prevBulkDownloading.current && !bulkDownloading) {
      setSelectedReportIds(new Set());
    }
    prevBulkDownloading.current = bulkDownloading;
  }, [bulkDownloading]);

  const handleBulkDownload = () => {
    if (selectedReportIds.size > BULK_DOWNLOAD_HARD_LIMIT) {
      toast.error(t('commissioning.downloadLimitExceeded', { limit: BULK_DOWNLOAD_HARD_LIMIT }));
      return;
    }
    dispatch(downloadBulkCommissioningPdf(Array.from(selectedReportIds)));
  };

  const handleSummaryExport = () => {
    const rawParams: Record<string, any> = {
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      unlinkedOnly: unlinkedOnly ? 'true' : undefined,
      order: sort || undefined,
      ...getFilterParams(),
    };
    const params = Object.fromEntries(
      Object.entries(rawParams).filter(([, v]) => v !== undefined && v !== null)
    );
    dispatch(downloadCommissioningSummaryPdf(params));
  };

  const csvExportLoading = useSelector(getExportReportsLoading);

  const handleExportCsvClick = () => {
    dispatch(fetchReportsCsvExport(selectedFilters, Map({ type: typeFilter, status: statusFilter, unlinkedOnly })));
  };


  const onClickLinkDevice = (e, reportId, reportType) => {
    e.stopPropagation();
    setLinkReportId(reportId);
    setLinkReportType(COMMISSIONING_DEVICE_TYPE_MAP[reportType]);
    setShowLinkDialog(true);
  };

  const handleLinkDevice = (deviceId) => {
    dispatch(linkDeviceToCheckSheet(linkReportId, deviceId));
    setShowLinkDialog(false);
    setLinkReportId(null);
    setLinkReportType(null);
  };

  // MUI injects styles via CSS-in-JS (emotion) at runtime with higher specificity than external SCSS.
  // Using sx prop instead of className ensures our overrides win — this is the correct way to style MUI components.
  const checkboxStyles = {
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': {
      fontSize: 22,
      borderRadius: '6px',
    },
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&.Mui-checked': {
      color: '#1976d2',
      '& .MuiSvgIcon-root': {
        filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))',
      },
    },
    '&.MuiCheckbox-indeterminate': {
      color: '#1976d2',
    },
  };

  const columns: any = useMemo(() => [
    ...(pdfServicesEnabled ? [{
      id: 'select',
      Header: () => (
        <Checkbox
          checked={reports.size > 0 && selectedReportIds.size === reports.size}
          indeterminate={selectedReportIds.size > 0 && selectedReportIds.size < reports.size}
          onChange={(e) => handleSelectAll(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          sx={checkboxStyles}
        />
      ),
      accessor: (row) => row.get('id'),
      Cell: (row: any) => (
        <Checkbox
          checked={selectedReportIds.has(row.cell.value)}
          onChange={() => handleToggleSelect(row.cell.value)}
          onClick={(e) => e.stopPropagation()}
          sx={checkboxStyles}
        />
      ),
    }] : []),
    {
      id: 'check_sheet_type',
      Header: t('commissioning.formType'),
      accessor: (row) => row.get('check_sheet_type'),
      Cell: (row: any) => t(COMMISSIONING_TYPE_LABEL_MAP[row.cell.value] || row.cell.value),
    },
    {
      id: 'device_tag',
      Header: t('commissioning.deviceTag'),
      accessor: (row) => row.get('device_tag'),
      Cell: (row: any) => {
        if (row.cell.value) {
          return ( <DeviceLinkCombo device={row.cell.row.original} />);
        }
        const reportTag = row.cell.row.original.get('report_tag');
        return (
          <div className='commissioning-row-actions'>
            {reportTag && <span className='commissioning-report-tag'>{reportTag}</span>}
            <Chip
              icon={<LinkIcon />}
              label={t('commissioning.linkDevice')}
              variant='outlined'
              size='small'
              onClick={(e) => onClickLinkDevice(e, row.cell.row.original.get('id'), row.cell.row.original.get('check_sheet_type'))}
              className={'commissioning-linkChip'}
            />
          </div>
        );
      },
    },
    {
      id: 'commissioning_status',
      Header: t('common.commissioningStatus'),
      accessor: (row) => row.get('commissioning_status'),
      Cell: (row: any) => <CommissioningStatusIcon status={row.cell.value} />,
    },
    {
      id: 'created_by',
      Header: t('testingReports.createdBy'),
      accessor: (row) => row.get('first_name'),
      Cell: (row: any) => `${row.cell.row.original.get('first_name') || ''} ${row.cell.row.original.get('last_name') || ''}`.trim(),
    },
    {
      id: 'created_at',
      Header: t('testingReports.createdAt'),
      accessor: (row) => row.get('created_at'),
      Cell: (row: any) => row.cell.value ? formatDate(row.cell.value) : '',
    },
    {
      id: 'open',
      Header: '',
      sortable: false,
      accessor: (row) => row.get('id'),
      Cell: (row: any) => (
        <div className='commissioning-row-actions'>
          <Tooltip title={t('commissioning.viewDetails')} placement='top'>
            <span>
              <Button
                compact
                icon={<VisibilityIcon style={{ color: variables.primaryFlat }} />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const original = row.cell.row.original;
                  const type = original.get('check_sheet_type');
                  const id = original.get('id');
                  setSelectedReportId(id);
                  setSelectedDeviceId(original.get('device_id') || null);
                  setSelectedType(type);
                  dispatch(openCheckSheet(type));
                }}
              />
            </span>
          </Tooltip>

          {pdfServicesEnabled && (
            <Tooltip title={t('commissioning.download')} placement='top'>
              <span>
                <PdfDownloadButton
                  reportId={row.cell.row.original.get('id')}
                  checkSheetType={row.cell.row.original.get('check_sheet_type')}
                  deviceTag={row.cell.row.original.get('device_tag') || ''}
                />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
  ], [t, pdfServicesEnabled, selectedReportIds, handleToggleSelect, handleSelectAll, reports]);

  const showActionBar = pdfServicesEnabled && selectedReportIds.size > 0;
  const selectionCount = selectedReportIds.size;
  const selectionLabel = selectionCount === 1 ? t('commissioning.report') : t('commissioning.reports');

  return (
    <div className='page' id='commissioning-screen'>
      <Helmet>
        <title>{t('nav.commissioning')}</title>
      </Helmet>
      <FilterBar includeGrid />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <div className={'commissioning-header'}>
              <div className={'commissioning-header-spacer'} />
              <h2>{t('nav.commissioning')}</h2>
              <div className={'commissioning-header-actions'}>
                <PermissionFence can='edit-check-sheet-report'>
                  <Button cta onClick={onClickCreate}>{t('commissioning.createNew')}</Button>
                </PermissionFence>
              </div>
            </div>
            <div className={'commissioning-filters'}>
              <FormControl size='small' className={'commissioning-filterControl'}>
                <InputLabel>{t('commissioning.formType')}</InputLabel>
                <Select value={typeFilter} label={t('commissioning.formType')} displayEmpty onChange={handleTypeFilterChange}>
                  <MenuItem value=''>{t('commissioning.allTypes')}</MenuItem>
                  <MenuItem value='commissioning-comm-loop-v2'>{t('commissioning.commLoop')}</MenuItem>
                  <MenuItem value='commissioning-circuit'>{t('commissioning.circuit')}</MenuItem>
                  <MenuItem value='commissioning-controller'>{t('commissioning.controller')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size='small' className={'commissioning-filterControl'}>
                <InputLabel>{t('common.commissioningStatus')}</InputLabel>
                <Select value={statusFilter} label={t('common.commissioningStatus')} displayEmpty onChange={handleStatusFilterChange}>
                  <MenuItem value=''>{t('commissioning.allStatuses')}</MenuItem>
                  {COMMISSIONING_STATUS_OPTIONS.map(({ value, labelKey }) => (
                    <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ToggleButton
                value='unlinkedOnly'
                selected={unlinkedOnly}
                onChange={() => { setUnlinkedOnly(!unlinkedOnly); setPage(0); }}
                size='small'
                className='commissioning-unlinkedOnly'
              >
                <FilterListIcon fontSize='small' className='commissioning-unlinkedOnly-icon' />
                {t('commissioning.unlinkedOnly')}
              </ToggleButton>
              <ExportMenu
                triggerClassName='commissioning-toolbar-option commissioning-export-btn'
                triggerLabel={t('commissioning.exportSummary')}
                options={[
                  {
                    key: 'csv',
                    label: t('downloadCsv'),
                    icon: <img src={CsvIcon} alt='' className='export-menu-file-icon' />,
                    loading: csvExportLoading,
                    disabled: csvExportLoading,
                    onClick: handleExportCsvClick,
                  },
                  {
                    key: 'pdf',
                    label: t('downloadPdf'),
                    icon: <img src={PdfIcon} alt='' className='export-menu-file-icon' />,
                    loading: summaryDownloading,
                    disabled: summaryDownloading,
                    hidden: !pdfServicesEnabled,
                    onClick: handleSummaryExport,
                  },
                ]}
              />
            </div>
            <DataTable
              filterable={false}
              pagination
              numPages={totalPages}
              totalRecords={total}
              loading={loading}
              data={List(reports)}
              columns={columns}
              fetchPage={fetchPage}
              tableId='commissioning-table'
              columnWidths={pdfServicesEnabled ? ['5%', '18%', '22%', '14%', '18%', '14%', '4%'] : ['20%', '25%', '15%', '20%', '20%']}
              placeholder={<span>{t('testingReports.noReports')}</span>}
            />
          </Paper>
        </Grid>
      </Grid>

      <CommissioningTypeSelectDialog
        open={showTypeDialog}
        onSelect={handleTypeSelected}
        onClose={() => setShowTypeDialog(false)}
      />

      <CommissioningCommLoopReport
        open={commLoopV2Open && selectedType === 'commissioning-comm-loop-v2'}
        reportId={selectedReportId}
        deviceId={selectedDeviceId}
        handleClose={() => handleClose('commissioning-comm-loop-v2')}
      />
      <CommissioningCircuitReport
        open={circuitOpen && selectedType === 'commissioning-circuit'}
        reportId={selectedReportId}
        deviceId={selectedDeviceId}
        handleClose={() => handleClose('commissioning-circuit')}
      />
      <CommissioningControllerReport
        open={controllerOpen && selectedType === 'commissioning-controller'}
        reportId={selectedReportId}
        deviceId={selectedDeviceId}
        handleClose={() => handleClose('commissioning-controller')}
      />

      <LinkDeviceDialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onConfirm={handleLinkDevice}
        deviceType={linkReportType}
      />

      {showActionBar && selectionCount > BULK_DOWNLOAD_WARN_LIMIT && (
        <div className='commissioning-bulk-warning'>
          {t('commissioning.downloadSlowWarning')}
        </div>
      )}
      {showActionBar && (
        <FloatingActionBar
          className='commissioning-floating-action-bar'
          label={`${selectionCount} ${selectionLabel} ${t('commissioning.selected')}`}
        >
          <Button className='clear-btn' onClick={() => setSelectedReportIds(new Set())} disabled={bulkDownloading}>
            {t('common.clear')}
          </Button>
          <Button
            icon={<FileDownloadIcon style={{ fontSize: 14 }} />}
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
            loading={bulkDownloading}
          >
            {t('commissioning.downloadSelected', { count: selectionCount })}
          </Button>
        </FloatingActionBar>
      )}
    </div>
  );
}
